import React, { useState, useEffect, useRef } from 'react';

import { collection, addDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { FileCheck, X, Star, Loader, ArrowLeft } from 'lucide-react';
import { createPortal } from 'react-dom';
import { motion, useInView } from 'framer-motion';

const Modal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div 
        className="fixed inset-0 bg-black/50 transition-opacity backdrop-blur-sm" 
        onClick={onClose}
      />
      <div className="relative z-50 bg-white rounded-xl shadow-2xl w-full max-w-lg mx-auto border border-gray-200 animate-slide-up">
        {children}
      </div>
    </div>,
    document.body
  );
};

const HomepageEligibilitySection = ({ showWave = true }) => {
  
  const [showEligibility, setShowEligibility] = useState(false);
  const [loading, setLoading] = useState(false);
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.2 });
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    debtAmount: '',
    termsAccepted: false
  });

  const debtAmountOptions = [
    { value: '0-50000', label: '0 - 50,000 ₪' },
    { value: '50000-170000', label: '50,000 - 170,000 ₪' },
    { value: '170000+', label: 'מעל 170,000 ₪' }
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await addDoc(collection(db, 'leads'), {
        ...formData,
        subject: 'בדיקת זכאות להסדרת חובות',
        type: 'eligibility_check',
        createdAt: new Date().toISOString()
      });
      
      setShowEligibility(false);
      setFormData({ fullName: '', phone: '', debtAmount: '', termsAccepted: false });
      window.location.href = '/thank-you';
    } catch (error) {
      console.error('Error submitting eligibility form:', error);
      alert('אירעה שגיאה בשליחת הבדיקה. אנא נסו שוב.');
    } finally {
      setLoading(false);
    }
  };

  const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
  };

  const titleVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, delay: 0.2 } }
  };

  const buttonVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.6, delay: 0.4 } }
  };

  return (
    <>
      <div
        className={`relative overflow-hidden bg-transparent ${showWave ? 'py-20' : 'py-6'}`}
        dir="rtl"
      >
        <motion.div
          ref={sectionRef}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          variants={sectionVariants}
          className="relative z-10 max-w-4xl mx-auto px-4 text-center"
        >
          <div className="bg-slate-50 rounded-2xl border border-gray-200 p-6 sm:p-8 shadow-xl max-w-2xl mx-auto">
          {/* Title */}
          <motion.div variants={titleVariants} className="mb-6 md:mb-8">
            <h2 className={`font-bold text-gray-800 mb-4 ${showWave ? 'text-3xl md:text-4xl lg:text-5xl' : 'text-2xl md:text-3xl'}`}>
              בדקו את זכאותכם עכשיו
            </h2>
            <p className={`text-gray-600 max-w-2xl mx-auto leading-relaxed ${showWave ? 'text-lg md:text-xl' : 'text-base md:text-lg'}`}>
              האם אתם זכאים להקלה בחובות ולפתיחת דף חדש? גלו תוך דקות ללא עלות
            </p>
          </motion.div>

          {/* Main CTA Button */}
          <motion.div variants={buttonVariants}>
            <button
              onClick={() => setShowEligibility(true)}
              className={`
                group relative bg-gray-800 text-white px-8 py-4 md:px-12 md:py-6 rounded-lg font-bold text-lg md:text-xl
                shadow-md hover:shadow-lg transform hover:-translate-y-1 transition-all duration-300
                w-full md:w-auto inline-flex items-center justify-center gap-3
              `}
            >
              <FileCheck className="w-6 h-6 group-hover:scale-110 transition-transform" />
              <span>בדיקת זכאות מהירה</span>
              <ArrowLeft className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </motion.div>

          {/* Features */}
          <div className="mt-8">
            <div className="flex flex-wrap justify-center items-center gap-6 md:gap-8">
              <div className="flex items-center gap-2 text-yellow-600">
                <Star className="w-4 h-4 fill-current text-yellow-500" />
                <span className="text-sm font-medium">ללא עלות</span>
              </div>
              <div className="w-px h-6 bg-gray-300 hidden md:block" />
              <div className="flex items-center gap-2 text-yellow-600">
                <FileCheck className="w-4 h-4 text-yellow-500" />
                <span className="text-sm font-medium">בדיקה מהירה</span>
              </div>
              <div className="w-px h-6 bg-gray-300 hidden md:block" />
              <div className="flex items-center gap-2 text-yellow-600">
                <X className="w-4 h-4 text-yellow-500" />
                <span className="text-sm font-medium">ללא התחייבות</span>
              </div>
            </div>
          </div>
            </div>
        </motion.div>

        {showWave && (
          <div className="absolute bottom-0 left-0 right-0">
            <svg viewBox="0 0 1200 120" className="w-full h-12 md:h-16 fill-current text-white">
              <path d="M0,60 C200,30 400,90 600,60 C800,30 1000,90 1200,60 L1200,120 L0,120 Z"></path>
            </svg>
          </div>
        )}
      </div>

      {/* Modal */}
      <Modal isOpen={showEligibility} onClose={() => setShowEligibility(false)}>
        <div className="p-6" dir="rtl">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                <FileCheck className="w-5 h-5 text-gray-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800">בדיקת זכאות</h3>
            </div>
            <button
              onClick={() => setShowEligibility(false)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-gray-700 text-sm leading-relaxed">
              נתקלתם בקשיים כלכליים, עיקולים או הגבלות? בדקו בקלות האם אתם זכאים להליך הסדרת חובות.
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">שם מלא</label>
              <input
                type="text"
                name="fullName"
                required
                value={formData.fullName}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-1 focus:ring-gray-400 focus:border-gray-400 outline-none transition-all"
                placeholder="הכנס שם מלא"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">טלפון</label>
              <input
                type="tel"
                name="phone"
                required
                value={formData.phone}
                onChange={handleInputChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-1 focus:ring-gray-400 focus:border-gray-400 outline-none transition-all"
                placeholder="הכנס מספר טלפון"
                dir="rtl"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">מה גובה החוב שלכם?</label>
              <div className="space-y-2">
                {debtAmountOptions.map((option) => (
                  <label key={option.value} className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="debtAmount"
                      value={option.value}
                      checked={formData.debtAmount === option.value}
                      onChange={handleInputChange}
                      required
                      className="sr-only"
                    />
                    <div className={`
                      w-full p-3 rounded-lg border transition-all duration-300 font-medium
                      ${formData.debtAmount === option.value 
                        ? 'bg-gray-100 border-gray-400 text-gray-800' 
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                      }
                    `}>
                      <span className="flex-1 text-center">{option.label}</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>

        <div className="flex items-start gap-3 pt-2">
  <input
    type="checkbox"
    name="termsAccepted"
    id="terms"
    required
    checked={formData.termsAccepted}
    onChange={handleInputChange}
    className="mt-1 w-4 h-4 text-gray-600 border-gray-300 rounded focus:ring-gray-500"
  />
  <label htmlFor="terms" className="text-sm text-gray-600 cursor-pointer leading-relaxed">
    אני מאשר/ת את{' '}
    <a 
      href="/terms" 
      target="_blank"
      className="text-gray-800 underline hover:text-gray-600 transition-colors font-medium"
    >
      תנאי השימוש
    </a>
    {' '}ו
    <a 
      href="/privacy" 
      target="_blank"
      className="text-gray-800 underline hover:text-gray-600 transition-colors font-medium"
    >
      מדיניות הפרטיות
    </a>
    {' '}של האתר וקבלת עדכונים ופניות מהמשרד בנושא בדיקת הזכאות והסדרת חובות
  </label>
</div>

<button
  type="submit"
  disabled={loading || !formData.termsAccepted}
  className={`
    w-full p-3 rounded-lg text-lg font-bold flex items-center justify-center gap-3
    ${loading || !formData.termsAccepted
      ? 'bg-gray-300 cursor-not-allowed opacity-50' 
      : 'bg-gray-800 hover:bg-gray-900'
    }
    text-white transition-all duration-300
  `}
>
  {loading ? (
    <>
      <Loader className="w-5 h-5 animate-spin" />
      <span>בודק זכאות...</span>
    </>
  ) : (
    <>
      <FileCheck className="w-5 h-5" />
      <span>בדק זכאות עכשיו</span>
    </>
  )}
</button>
          </form>
        </div>
      </Modal>
    </>
  );
};

export default HomepageEligibilitySection;
